import { useCallback, useMemo } from "react";
import type { Option } from "../../common/SelectBase";

const SELECT_ALL_TEXT = "Select all";
const SELECT_RELEVANT_TEXT = "Select relevant";

interface UseSelectMultipleReturn {
  /**
   * Whether all filtered options are selected
   */
  allSelected: boolean;

  /**
   * Whether all relevant filtered options are selected
   */
  relevantSelected: boolean;

  /**
   * Callback to toggle selection of all filtered options
   */
  toggleAllFiltered: () => void;

  /**
   * Text to display for the select all/relevant option
   */
  displayText: typeof SELECT_ALL_TEXT | typeof SELECT_RELEVANT_TEXT;
}

/**
 * Hook to manage "select all" or "select relevant" functionality for a list of options.
 *
 * @param selectedValues - Currently selected option values
 * @param filteredOptions - Options currently filtered/displayed
 * @param onChange - Callback to update selected values
 */
export function useSelectMultiple(
  selectedValues: string[],
  filteredOptions: Option[],
  onChange: (next: string[]) => void
): UseSelectMultipleReturn {
  // A locked option is outside this row's remit: it keeps whatever state it is
  // in, so select-all neither adds nor removes it.
  const toggleable = useMemo(
    () => filteredOptions.filter((option) => !option.disabled),
    [filteredOptions]
  );

  const hasRelevantOptions = toggleable.some(
    (option) => option.relevant === true
  );

  const allOptionsRelevant =
    hasRelevantOptions &&
    toggleable.every((option) => option.relevant === true);

  const allSelected = useMemo(
    () =>
      toggleable.length > 0 &&
      toggleable.every((option) => selectedValues.includes(option.value)),
    [toggleable, selectedValues]
  );

  // If every filteredOption that has relevant is found in selectedValues
  const relevantSelected = useMemo(() => {
    const relevantFiltered = toggleable.filter((option) => option.relevant);

    if (relevantFiltered.length === 0) {
      return false;
    }

    if (
      relevantFiltered.every((option) => selectedValues.includes(option.value))
    ) {
      return true;
    }

    return false;
  }, [toggleable, selectedValues]);

  /**
   * Toggles selection of all filtered options.
   * - If all filtered options are selected, deselects them.
   * - Otherwise, selects all filtered options (preserving already selected values).
   */
  const toggleAllFiltered = useCallback(() => {
    if (!toggleable.length) {
      return;
    }

    const toggleableValues = toggleable.map((option) => option.value);
    const allSelected = toggleableValues.every((value) =>
      selectedValues.includes(value)
    );

    if (allSelected) {
      // Locked values are not in `toggleableValues`, so this keeps them.
      onChange(
        selectedValues.filter((value) => !toggleableValues.includes(value))
      );
    } else if (
      selectedValues.length === 0 &&
      hasRelevantOptions &&
      !allOptionsRelevant
    ) {
      onChange(
        toggleable
          .filter((value) => value.relevant)
          .map((option) => option.value)
      );
    } else {
      // Merge existing selected values with filtered values, avoiding duplicates
      onChange(Array.from(new Set([...selectedValues, ...toggleableValues])));
    }
  }, [
    toggleable,
    onChange,
    selectedValues,
    allOptionsRelevant,
    hasRelevantOptions
  ]);

  const displayText = useMemo(() => {
    if (
      selectedValues.length === 0 &&
      hasRelevantOptions &&
      !allOptionsRelevant
    ) {
      return SELECT_RELEVANT_TEXT;
    }

    return SELECT_ALL_TEXT;
  }, [selectedValues, allOptionsRelevant, hasRelevantOptions]);

  return {
    allSelected,
    relevantSelected,
    toggleAllFiltered,
    displayText
  };
}
